import React, { useEffect, useState } from 'react'
import {
	Box,
	Title,
	TextInput,
	Button,
	Group,
	Text,
	FileInput,
} from '@mantine/core'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getOrgDetails } from '../services/actions/orgActions'
import { updateOrgDetails } from '../services/actions/controlActions'
import { switchOrg } from '../services/actions/userActions'
import { loadUserContext } from '../services/utils/loadUserContext'
import { uploadOrgLogoToS3 } from '../services/utils/uploadImageToS3'
import ImageCropperModal from '../components/ImageCropperModal'
import { readFileAsDataUrl } from '../services/utils/imageCropper'

const ORG_LOGO_TARGET_SIZE = 512

const ControlOrgDetailsPage = () => {
	const { orgSlug } = useParams()
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const currentOrgSlug = useSelector((state) => state.user.currentOrg?.orgSlug)

	const [name, setName] = useState('')
	const [sendgridFromEmail, setSenderEmail] = useState('')
	const [logo, setLogo] = useState('')
	const [logoCropOpened, setLogoCropOpened] = useState(false)
	const [logoCropSourceUrl, setLogoCropSourceUrl] = useState('')
	const [uploadingLogo, setUploadingLogo] = useState(false)
	const [error, setError] = useState('')
	const [notice, setNotice] = useState('')

	const [loading, setLoading] = useState(true)

	useEffect(() => {
		const fetchOrg = async () => {
			try {
				const org = await getOrgDetails(orgSlug)
				setName(org.name || '')
				setSenderEmail(org.sendgridFromEmail || '')
				setLogo(org.logo || '')
			} catch (err) {
				console.error('Failed to load org:', err)
				setError('Failed to load organization details.')
			} finally {
				setLoading(false)
			}
		}

		fetchOrg()
	}, [orgSlug])

	const handleSave = async () => {
		try {
			setError('')
			setNotice('')
			await updateOrgDetails(orgSlug, {
				name,
				sendgridFromEmail,
				logo,
			})
			setNotice('Organization details updated.')
		} catch (err) {
			console.error('Failed to update org:', err)
			setError(err.message || 'Update failed.')
		}
	}

	const handleManageOrg = async () => {
		try {
			if (currentOrgSlug !== orgSlug) {
				await switchOrg(orgSlug)
				await loadUserContext(dispatch)
			}
			navigate(`/${orgSlug}/admin/experiences`)
		} catch (err) {
			console.error('Failed to switch organization:', err)
			setError('Failed to switch organization.')
		}
	}

	const handleSelectLogo = async (file) => {
		if (!file) return
		try {
			setError('')
			setNotice('')
			const sourceUrl = await readFileAsDataUrl(file)
			setLogoCropSourceUrl(sourceUrl)
			setLogoCropOpened(true)
		} catch (err) {
			setError(err.message || 'Failed to load logo for cropping.')
		}
	}

	const handleRecropLogo = () => {
		if (!logo) return
		setLogoCropSourceUrl(logo)
		setLogoCropOpened(true)
	}

	const handleApplyLogoCrop = async (blob) => {
		if (!blob) return
		try {
			setError('')
			setNotice('')
			setUploadingLogo(true)
			const file = new File([blob], `org-logo-${Date.now()}.jpg`, {
				type: 'image/jpeg',
			})
			const url = await uploadOrgLogoToS3(file, orgSlug)
			setLogo(url)
			setLogoCropOpened(false)
			setNotice('Logo uploaded. Save organization details to apply.')
		} catch (err) {
			setError(err.message || 'Failed to upload logo.')
		} finally {
			setUploadingLogo(false)
		}
	}

	const handleRemoveLogo = () => {
		setLogo('')
		setLogoCropSourceUrl('')
		setLogoCropOpened(false)
	}

	if (loading)
		return (
			<Box px='md' py='md'>
				Loading...
			</Box>
		)

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Edit Organization Details
			</Title>

			<Group mb='md'>
				<Button
					variant='outline'
					onClick={() => navigate(`/control/orgs/${orgSlug}/billing`)}
				>
					Open Billing Controls
				</Button>
				<Button
					variant='outline'
					onClick={() => navigate(`/control/orgs/${orgSlug}/commerce`)}
				>
					Open Commerce Controls
				</Button>
				<Button
					variant='light'
					onClick={handleManageOrg}
				>
					Manage {name || 'Organization'}
				</Button>
			</Group>

			{error && (
				<Text c='red' mb='sm'>
					{error}
				</Text>
			)}
			{notice && (
				<Text c='teal' mb='sm'>
					{notice}
				</Text>
			)}

			<TextInput
				label='Organization Name'
				value={name}
				onChange={(e) => setName(e.target.value)}
				mb='sm'
				required
			/>

			<TextInput
				label='Sender Email'
				value={sendgridFromEmail}
				onChange={(e) => setSenderEmail(e.target.value)}
				mb='sm'
			/>

			<FileInput
				label={`Organization Logo (${ORG_LOGO_TARGET_SIZE}px by ${ORG_LOGO_TARGET_SIZE}px)`}
				placeholder='Choose logo image'
				accept='image/png,image/jpeg,image/webp'
				onChange={handleSelectLogo}
				disabled={uploadingLogo}
				mb='sm'
			/>

			{logo && (
				<Box mb='sm'>
					<Group position='apart' align='center' mb={6}>
						<Text size='xs' c='dimmed'>
							Current Logo
						</Text>
						<Group spacing='xs'>
							<Button
								variant='light'
								size='xs'
								onClick={handleRecropLogo}
								disabled={uploadingLogo}
							>
								Re-crop
							</Button>
							<Button
								variant='subtle'
								color='red'
								size='xs'
								onClick={handleRemoveLogo}
								disabled={uploadingLogo}
							>
								Remove Logo
							</Button>
						</Group>
					</Group>
					<Box
						style={{
							width: 140,
							height: 140,
							border: '1px solid #dee2e6',
							borderRadius: 8,
							overflow: 'hidden',
							backgroundColor: '#f8f9fa',
						}}
					>
						<img
							src={logo}
							alt='Organization logo'
							style={{
								display: 'block',
								width: '100%',
								height: '100%',
								objectFit: 'cover',
							}}
						/>
					</Box>
				</Box>
			)}

			<Group position='left' mt='lg'>
				<Button onClick={handleSave} disabled={uploadingLogo}>
					Save
				</Button>
			</Group>

			<ImageCropperModal
				opened={logoCropOpened}
				onClose={() => setLogoCropOpened(false)}
				title='Crop Logo'
				sourceUrl={logoCropSourceUrl}
				targetWidth={ORG_LOGO_TARGET_SIZE}
				targetHeight={ORG_LOGO_TARGET_SIZE}
				onApply={handleApplyLogoCrop}
				applyLabel='Use Cropped Logo'
				processing={uploadingLogo}
			/>
		</Box>
	)
}

export default ControlOrgDetailsPage
